import { useState } from 'react';
import {
  Camera,
  CameraResultType,
  CameraSource,
  ImageOptions,
} from '@capacitor/camera';
import type { CameraPermissionState } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

type SourceType = 'camera' | 'gallery' | 'prompt';

const isPermissionGranted = (permission: CameraPermissionState): boolean =>
  permission === 'granted' || permission === 'limited';

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });

const pickAndroidGalleryImage = (): Promise<string | null> =>
  new Promise((resolve, reject) => {
    const input = document.createElement('input');
    let settled = false;

    const cleanup = () => {
      window.removeEventListener('focus', handleFocus);
      input.remove();
    };

    const settle = (value: string | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };

    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    function handleFocus() {
      setTimeout(() => {
        if (!settled && !input.files?.length) {
          settle(null);
        }
      }, 800);
    }

    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';

    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) {
        settle(null);
        return;
      }

      try {
        settle(await fileToDataUrl(file));
      } catch (error) {
        fail(error);
      }
    });

    input.addEventListener('cancel', () => settle(null));
    window.addEventListener('focus', handleFocus);

    document.body.appendChild(input);
    input.click();
  });

const useCamera = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const requestPermissions = async (sourceType: SourceType) => {
    if (!Capacitor.isNativePlatform()) return;

    const platform = Capacitor.getPlatform();
    const status = await Camera.checkPermissions();

    if (sourceType === 'gallery') {
      if (platform !== 'ios' || isPermissionGranted(status.photos)) return;

      const request = await Camera.requestPermissions({ permissions: ['photos'] });
      if (!isPermissionGranted(request.photos)) {
        throw new Error('Permiso de galería denegado');
      }
      return;
    }

    if (status.camera !== 'granted') {
      const request = await Camera.requestPermissions({ permissions: ['camera'] });
      if (request.camera !== 'granted') {
        throw new Error('Permiso de cámara denegado');
      }
    }
  };

  const parseError = (err: any): string => {
    const msg = err?.message ?? String(err);
    if (
      msg.toLowerCase().includes('cancel') ||
      msg.toLowerCase().includes('user cancelled')
    ) {
      return 'Usuario canceló la acción';
    }
    return msg;
  };

  const takePicture = async (
    sourceType: SourceType,
    options: Partial<ImageOptions> = {}
  ): Promise<string | null> => {
    setIsLoading(true);
    setError(null);

    try {
      if (sourceType === 'gallery' && Capacitor.getPlatform() === 'android') {
        return await pickAndroidGalleryImage();
      }

      await requestPermissions(sourceType);

      const sourceMap = {
        camera: CameraSource.Camera,
        gallery: CameraSource.Photos,
        prompt: CameraSource.Prompt,
      };

      const photo = await Camera.getPhoto({
        quality: 60,
        resultType: CameraResultType.DataUrl,
        source: sourceMap[sourceType],
        promptLabelPicture: 'Tomar una foto nueva',
        promptLabelPhoto: 'Abrir la galería',
        allowEditing: false,
        ...options,
      });

      return photo.dataUrl ?? null;
    } catch (err: any) {
      const msg = parseError(err);
      // console.warn('Error al tomar la foto:', msg);
      setError(msg);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => setError(null);

  return {
    takePicture,
    isLoading,
    error,
    clearError,
  };
};

export default useCamera;
